import { useState } from "react";
import { CircleAlert, Info } from "lucide-react";
import Modal from "./Modal";

const MIN_PASSWORD_LENGTH = 8;

const emptyUser = {
  username: "",
  full_name: "",
  is_active: true,
  is_admin: false,
  can_manage_users: false,
  can_manage_applications: false,
  application_ids: [],
};

/**
 * Create/edit form for one account.
 *
 * `lockPermissions` is set when editing your own account: the API refuses those changes so you
 * cannot lock yourself out, and the form must not pretend otherwise.
 */
export default function UserFormModal({
  user = null,
  applications,
  busy = false,
  error = "",
  lockPermissions = false,
  onSubmit,
  onClose,
}) {
  const isEdit = user !== null;
  const [form, setForm] = useState(() => ({
    ...emptyUser,
    ...(user ?? {}),
    full_name: user?.full_name ?? "",
    application_ids: user?.application_ids ?? [],
  }));
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState("");

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const toggleApplication = (id) =>
    set({
      application_ids: form.application_ids.includes(id)
        ? form.application_ids.filter((value) => value !== id)
        : [...form.application_ids, id],
    });

  // An admin implies every permission on the backend, so show that rather than let the
  // checkboxes disagree with reality.
  const impliedByAdmin = form.is_admin;

  const handleSubmit = (event) => {
    event.preventDefault();
    setLocalError("");

    if (!isEdit) {
      if (!form.username.trim()) {
        setLocalError("نام کاربری را وارد کنید.");
        return;
      }
      if (password.length < MIN_PASSWORD_LENGTH) {
        setLocalError(`رمز عبور باید حداقل ${MIN_PASSWORD_LENGTH} کاراکتر باشد.`);
        return;
      }
    }

    const payload = {
      full_name: form.full_name.trim() || null,
      is_admin: form.is_admin,
      is_active: form.is_active,
      can_manage_users: form.is_admin || form.can_manage_users,
      can_manage_applications: form.is_admin || form.can_manage_applications,
      application_ids: form.application_ids,
    };
    onSubmit(isEdit ? payload : { ...payload, username: form.username.trim(), password });
  };

  const shownError = localError || error;

  return (
    <Modal
      title={isEdit ? `ویرایش کاربر «${user.username}»` : "کاربر جدید"}
      description="دسترسی‌های این کاربر را در همین فرم مشخص کنید."
      onClose={onClose}
      wide
      footer={
        <>
          <button type="submit" form="user-form" className="btn btn-primary" disabled={busy}>
            {busy ? "در حال ذخیره…" : "ذخیره"}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            انصراف
          </button>
        </>
      }
    >
      {shownError && (
        <div className="alert alert-error">
          <CircleAlert size={17} strokeWidth={2} aria-hidden="true" />
          {shownError}
        </div>
      )}

      <form id="user-form" onSubmit={handleSubmit} noValidate>
        <div className="field-row">
          <div className="field">
            <label htmlFor="user-username">نام کاربری</label>
            <input
              id="user-username"
              type="text"
              className="ltr-input"
              value={form.username}
              onChange={(e) => set({ username: e.target.value })}
              disabled={isEdit}
              autoComplete="off"
            />
            {isEdit && <span className="hint">نام کاربری پس از ساخت قابل تغییر نیست.</span>}
          </div>

          <div className="field">
            <label htmlFor="user-fullname">نام و نام خانوادگی</label>
            <input
              id="user-fullname"
              type="text"
              value={form.full_name}
              onChange={(e) => set({ full_name: e.target.value })}
              autoComplete="off"
            />
          </div>
        </div>

        {!isEdit && (
          <div className="field">
            <label htmlFor="user-password">رمز عبور</label>
            <input
              id="user-password"
              type="password"
              className="ltr-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <span className="hint">حداقل {MIN_PASSWORD_LENGTH} کاراکتر.</span>
          </div>
        )}

        {lockPermissions && (
          <div className="alert alert-info">
            <Info size={17} strokeWidth={2} aria-hidden="true" />
            دسترسی‌ها و وضعیت حساب خودتان قابل تغییر نیست.
          </div>
        )}

        <fieldset className="fieldset" disabled={lockPermissions}>
          <legend>دسترسی‌ها</legend>

          <label className="check">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set({ is_active: e.target.checked })}
            />
            <span>
              <strong>حساب فعال</strong>
              <small>کاربر غیرفعال نمی‌تواند وارد سیستم شود.</small>
            </span>
          </label>

          <label className="check">
            <input
              type="checkbox"
              checked={form.is_admin}
              onChange={(e) => set({ is_admin: e.target.checked })}
            />
            <span>
              <strong>مدیر سیستم</strong>
              <small>دسترسی کامل به همه بخش‌ها و همه اپلیکیشن‌ها.</small>
            </span>
          </label>

          <label className={`check${impliedByAdmin ? " implied" : ""}`}>
            <input
              type="checkbox"
              checked={impliedByAdmin || form.can_manage_users}
              disabled={impliedByAdmin}
              onChange={(e) => set({ can_manage_users: e.target.checked })}
            />
            <span>
              <strong>مدیریت کاربران</strong>
              <small>ساخت، ویرایش و حذف کاربران و تعیین دسترسی آن‌ها.</small>
            </span>
          </label>

          <label className={`check${impliedByAdmin ? " implied" : ""}`}>
            <input
              type="checkbox"
              checked={impliedByAdmin || form.can_manage_applications}
              disabled={impliedByAdmin}
              onChange={(e) => set({ can_manage_applications: e.target.checked })}
            />
            <span>
              <strong>مدیریت اپلیکیشن‌ها</strong>
              <small>افزودن، ویرایش و حذف اپلیکیشن‌ها.</small>
            </span>
          </label>
        </fieldset>

        <fieldset className="fieldset" disabled={lockPermissions}>
          <legend>دسترسی به اپلیکیشن‌ها</legend>

          {impliedByAdmin && (
            <p className="fieldset-note">
              مدیر سیستم به همه اپلیکیشن‌ها دسترسی دارد، حتی موارد انتخاب‌نشده.
            </p>
          )}

          {applications.length === 0 ? (
            <p className="fieldset-note">اپلیکیشنی ثبت نشده است.</p>
          ) : (
            <div className="check-grid">
              {applications.map((application) => (
                <label className="check" key={application.id}>
                  <input
                    type="checkbox"
                    checked={form.application_ids.includes(application.id)}
                    onChange={() => toggleApplication(application.id)}
                  />
                  <span>
                    <strong>{application.title}</strong>
                    <small className="ltr-text">{application.path}</small>
                  </span>
                </label>
              ))}
            </div>
          )}
        </fieldset>
      </form>
    </Modal>
  );
}
